import React, { useState } from 'react'
import { Phone, Mail, ArrowRight, Building2 } from 'lucide-react'
import Swal from 'sweetalert2'
import { Helmet } from 'react-helmet-async'

const API_URL = process.env.REACT_APP_BACKEND_URL

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  })

  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = e => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.subject.trim() ||
      !formData.message.trim()
    ) {
      Swal.fire({
        icon: 'warning',
        title: 'All fields are required',
        confirmButtonColor: '#D32C30'
      })
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch(`${API_URL}contact/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      const result = await response.json()

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Message sent successfully!',
          text: 'Thank you for contacting S Infinite Express. Our team will get back to you soon.',
          confirmButtonColor: '#211F57'
        })

        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        })
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Failed to submit',
          text: result.message || 'Something went wrong',
          confirmButtonColor: '#D32C30'
        })
      }
    } catch (error) {
      console.error('Contact form error:', error)

      Swal.fire({
        icon: 'error',
        title: 'Server Error',
        text: 'Please try again later.',
        confirmButtonColor: '#D32C30'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const contactDetails = [
    {
      icon: Building2,
      title: 'Registered Office',
      value: (
        <>
          Office No. 12, Infinity Square, Opp. Varun Industries, Golani Naka,
          Vasai East, Maharashtra - 401208
        </>
      ),
      color: 'text-[#D32C30]',
      bg: 'bg-red-50'
    },
    {
      icon: Building2,
      title: 'Corporate Office',
      value: (
        <>
          Comet Building, Shop No. 12(B), Opp. Bandra Talav, Gurunanak Road,
          Bandra West - 400050
        </>
      ),
      color: 'text-blue-600',
      bg: 'bg-blue-50'
    },
    {
      icon: Phone,
      title: 'Call Our Team',
      value: '+91 82373 23531',
      color: 'text-green-600',
      bg: 'bg-green-50',
      link: 'tel:+918237323531'
    },
    {
      icon: Mail,
      title: 'Email Us',
      value: 'support@sinfiniteexpress.com',
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      link: 'mailto:support@sinfiniteexpress.com'
    }
  ]

  return (
    <>
      <Helmet>
        <title>Contact Us | S INFINITE EXPRESS PVT. LTD.</title>

        <meta
          name='description'
          content='Need assistance or have logistics inquiries? Contact S INFINITE EXPRESS for fast, reliable support regarding PTL, FTL, Express Delivery, warehousing, and nationwide courier services.'
        />

        <meta
          name='keywords'
          content='Contact S INFINITE EXPRESS, logistics support India, PTL support, FTL freight help, courier customer service, warehousing assistance, shipment help, logistics contact, logistics inquiry India, customer care logistics, express delivery contact, cargo contact, freight enquiry, courier service contact number'
        />

        <meta
          property='og:title'
          content='Contact Us | S INFINITE EXPRESS PVT. LTD.'
        />

        <meta
          property='og:description'
          content='Reach out to S INFINITE EXPRESS for logistics queries, shipment support, freight details, and contact information for our courier services.'
        />

        <meta property='og:type' content='website' />

        <meta
          property='og:url'
          content='https://sinfiniteexpress.com/contact-us'
        />

        <link rel='canonical' href='https://sinfiniteexpress.com/contact-us' />
      </Helmet>

      <main className='w-full bg-[#f7f8fc] overflow-hidden'>
        {/* CONTACT SECTION */}
        <section className='px-5 sm:px-8 lg:px-6 mb-4'>
          <div className='max-w-7xl mx-auto'>
            {/* Section Heading */}
            <div className='text-center max-w-4xl mx-auto mb-10'>
              <span className='inline-flex items-center gap-2 text-[#D32C30] text-xs font-extrabold uppercase tracking-[2px] mb-2 mt-6'>
                Get In Touch
              </span>

              <h2 className='text-3xl sm:text-4xl font-extrabold text-[#211F57] mb-2'>
                We'd Love To Hear From You
              </h2>

              <p className='text-gray-500 text-sm sm:text-base leading-relaxed'>
                Whether you need shipment assistance, a business quotation or
                information about our services, our team is ready to help.
              </p>
            </div>

            <div className='grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5 items-start'>
              {/* CONTACT FORM */}
              <div
                id='contact-form'
                className='lg:col-span-7 bg-white rounded-3xl shadow-[0_15px_60px_rgba(33,31,87,0.08)] border border-gray-100 overflow-hidden'
              >
                <div className='bg-[#211F57] px-4 sm:px-5 py-4 relative overflow-hidden'>
                  <div className='absolute -right-10 -top-16 w-48 h-48 rounded-full border-[25px] border-white/10' />

                  <div className='relative flex items-center gap-4'>
                    <div>
                      <h2 className='text-xl sm:text-2xl font-extrabold text-white'>
                        Send Us a Message
                      </h2>

                      <p className='text-gray-300 text-xs sm:text-sm mt-1'>
                        Fill out the form and we'll get back to you.
                      </p>
                    </div>
                  </div>
                </div>

                <form className='p-6 sm:p-10 space-y-6' onSubmit={handleSubmit}>
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-5'>
                    {/* Name */}
                    <div>
                      <label className='block text-sm font-bold text-[#211F57] mb-2'>
                        Full Name <span className='text-[#D32C30]'>*</span>
                      </label>

                      <input
                        type='text'
                        name='name'
                        placeholder='Enter your full name'
                        value={formData.name}
                        onChange={handleChange}
                        required
                        maxLength={100}
                        autoComplete='name'
                        className='w-full px-4 py-3.5 rounded-xl bg-[#f8f9fc] border border-gray-200 text-sm text-gray-800 placeholder:text-gray-400 outline-none transition-all focus:bg-white focus:border-[#211F57] focus:ring-4 focus:ring-[#211F57]/5'
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className='block text-sm font-bold text-[#211F57] mb-2'>
                        Email Address <span className='text-[#D32C30]'>*</span>
                      </label>

                      <input
                        type='email'
                        name='email'
                        placeholder='Enter your email'
                        value={formData.email}
                        onChange={handleChange}
                        required
                        maxLength={150}
                        autoComplete='email'
                        className='w-full px-4 py-3.5 rounded-xl bg-[#f8f9fc] border border-gray-200 text-sm text-gray-800 placeholder:text-gray-400 outline-none transition-all focus:bg-white focus:border-[#211F57] focus:ring-4 focus:ring-[#211F57]/5'
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className='block text-sm font-bold text-[#211F57] mb-2'>
                      Subject <span className='text-[#D32C30]'>*</span>
                    </label>

                    <input
                      type='text'
                      name='subject'
                      placeholder='How can we help you?'
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      maxLength={200}
                      className='w-full px-4 py-3.5 rounded-xl bg-[#f8f9fc] border border-gray-200 text-sm text-gray-800 placeholder:text-gray-400 outline-none transition-all focus:bg-white focus:border-[#211F57] focus:ring-4 focus:ring-[#211F57]/5'
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label className='block text-sm font-bold text-[#211F57] mb-2'>
                      Your Message <span className='text-[#D32C30]'>*</span>
                    </label>

                    <textarea
                      name='message'
                      rows={5}
                      placeholder='Tell us about your requirements...'
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={2000}
                      className='w-full px-4 py-3.5 rounded-xl bg-[#f8f9fc] border border-gray-200 text-sm text-gray-800 placeholder:text-gray-400 outline-none transition-all resize-none focus:bg-white focus:border-[#211F57] focus:ring-4 focus:ring-[#211F57]/5'
                    />
                  </div>

                  {/* Submit */}
                  <button
                    type='submit'
                    disabled={isSubmitting}
                    className='w-full sm:w-auto inline-flex justify-center items-center gap-3 bg-[#D32C30] hover:bg-[#211F57] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-sm px-9 py-4 rounded-xl transition-all duration-300 shadow-lg shadow-red-600/10 hover:shadow-xl'
                  >
                    {isSubmitting ? (
                      <>
                        <span className='w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin' />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Message
                        <ArrowRight size={17} />
                      </>
                    )}
                  </button>
                </form>
              </div>

              {/* CONTACT INFORMATION */}
              <div className='lg:col-span-5 space-y-2'>
                <div className='mb-3'>
                  <span className='text-[#D32C30] text-xs font-extrabold uppercase tracking-[2px]'>
                    Contact Information
                  </span>

                  <h2 className='text-2xl sm:text-3xl font-extrabold text-[#211F57] mt-2'>
                    Let's Start A Conversation
                  </h2>

                  <p className='text-gray-500 text-xs leading-relaxed mt-2'>
                    Reach out to us through any of the following channels. We're
                    always happy to assist you.
                  </p>
                </div>

                {/* Contact Cards */}
                <div className='space-y-4'>
                  {contactDetails.map((item, index) => {
                    const Icon = item.icon

                    const content = (
                      <div className='flex items-start gap-4 p-5 sm:p-6 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-lg hover:border-[#211F57]/20 transition-all duration-300 group'>
                        <div
                          className={`${item.bg} ${item.color} w-12 h-12 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform`}
                        >
                          <Icon size={22} />
                        </div>

                        <div className='min-w-0'>
                          <h3 className='text-sm font-extrabold text-[#211F57] mb-2'>
                            {item.title}
                          </h3>

                          <div className='text-gray-600 text-sm leading-relaxed font-medium whitespace-normal break-normal'>
                            {item.value}
                          </div>
                        </div>

                        {item.link && (
                          <ArrowRight
                            size={17}
                            className='ml-auto mt-1 text-gray-300 group-hover:text-[#D32C30] transition-colors shrink-0'
                          />
                        )}
                      </div>
                    )

                    return item.link ? (
                      <a key={index} href={item.link} className='block'>
                        {content}
                      </a>
                    ) : (
                      <div key={index}>{content}</div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* GOOGLE MAP SECTION */}
        <section className='px-5 sm:px-8 lg:px-6 mb-4'>
          <div className='max-w-7xl mx-auto'>
            <div className='flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-7'>
              <div>
                <span className='text-[#D32C30] text-xs font-extrabold uppercase tracking-[2px]'>
                  Find Us
                </span>

                <h2 className='text-3xl font-extrabold text-[#211F57]'>
                  Visit Our Location
                </h2>

                <p className='text-gray-500 text-sm'>
                  Find us at our corporate office in Vasai, Mumbai.
                </p>
              </div>

              <a
                href='https://www.google.com/maps/search/?api=1&query=S+INFINITE+EXPRESS+PVT+LTD+Vasai+East+Mumbai'
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center gap-2 text-sm font-bold text-[#211F57] hover:text-[#D32C30] transition-colors'
              >
                Get Directions
                <ArrowRight size={17} />
              </a>
            </div>

            <div className='relative overflow-hidden rounded-3xl border border-gray-200 shadow-xl bg-white'>
              <iframe
                title='S Infinite Express Office Location'
                src='https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2251.4450728491242!2d72.85542089983836!3d19.41134190402857!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7a8d6af7a1e93%3A0x8fb5deca9762c74!2sS%20INFINITE%20EXPRESS%20PVT%20LTD!5e1!3m2!1sen!2sin!4v1782811232693!5m2!1sen!2sin'
                width='100%'
                height='420'
                allowFullScreen
                loading='lazy'
                referrerPolicy='no-referrer-when-downgrade'
                className='w-full h-[320px] sm:h-[420px]'
              />
            </div>
          </div>
        </section>

        {/* BOTTOM CTA */}
        <section className='px-5 sm:px-8 lg:px-6 mb-4'>
          <div className='max-w-7xl mx-auto bg-[#FFF44F] rounded-3xl p-8 sm:p-12 relative overflow-hidden'>
            <div className='absolute -right-16 -top-24 w-72 h-72 border-[35px] border-[#211F57]/5 rounded-full' />

            <div className='relative flex flex-col md:flex-row md:items-center justify-between gap-7'>
              <div className='max-w-2xl'>
                <span className='text-[#D32C30] text-xs font-extrabold uppercase tracking-[2px]'>
                  Your Logistics Partner
                </span>

                <h2 className='text-1xl sm:text-2xl lg:text-3xl font-extrabold text-[#211F57] mt-3'>
                  Ready to Move Your Business Forward?
                </h2>

                <p className='text-[#211F57]/70 text-sm sm:text-base mt-3'>
                  Let's build a smarter and more reliable supply chain together.
                </p>
              </div>

              <a
                href='tel:+918237323531'
                className='inline-flex items-center justify-center gap-3 bg-[#211F57] hover:bg-[#D32C30] text-white px-8 py-4 rounded-xl font-bold text-sm transition-all duration-300 shadow-lg shrink-0'
              >
                <Phone size={18} />
                Talk To Our Team
                <ArrowRight size={17} />
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  )
}

export default ContactPage
